import { Controller, useFormContext } from 'react-hook-form';
import { DateValue, parseAbsolute, parseZonedDateTime, toZoned } from '@internationalized/date';
import { IoMdTime } from 'react-icons/io';
import { TmdxDatepicker } from '@ds';
import { CommonRouteLegSchemaValues } from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import { useLocationDateTime } from '@lp/features/RoutePlanner/components/RouteLegForm/hooks';
import { AirportLocationProvider } from '@lp/features/RoutePlanner/components/RouteLegForm/context/AirportLocationContext';
import { RefuelAirportsBanner } from '@lp/features/RoutePlanner/components/RouteLegForm/components/banners';
import { useLegFormContext } from '@lp/features/RoutePlanner/components/RouteLegForm/context/LegFormContext';
import { CalculateGroundTimeType } from '@/api/apis/here-maps';
import { LegLocationTypeEnum, LegTypeEnum, PredefinedPlaceType, RouteLegTypeEnum } from '@/api/apis/route';
import { useWatchFields } from '@/hooks/useWatchFields';
import { validateDateTimeValue } from '@/utils/helpers';
import { SelectItemType } from '@/types';
import { AirportLocation } from './AirportLocation';
import { SegmentLocation } from './SegmentLocation';

type ArrivalLocationProps = {
  predefinedLocations: PredefinedPlaceType[];
  countriesList: SelectItemType[];
};

export const ArrivalLocation = ({ predefinedLocations, countriesList }: ArrivalLocationProps) => {
  const { isMainDataEdit, calculateLegTimeDuration, setLegDuration } = useLegFormContext();
  const { control, setValue } = useFormContext<CommonRouteLegSchemaValues>();
  const { departureLocation, arrivalLocation, formType, legType, timeDurationInMs, isDateTimeLinked } =
    useWatchFields<CommonRouteLegSchemaValues>([
      'departureLocation',
      'arrivalLocation',
      'formType',
      'legType',
      'timeDurationInMs',
      'isDateTimeLinked',
    ] as const);

  const { handleArrivalLocationDateTime } = useLocationDateTime();

  const handleDateTimeChange = async (date: DateValue | null) => {
    if (arrivalLocation.timezone) {
      const dateValue = validateDateTimeValue(date);
      const zonedDateValue = dateValue ? toZoned(dateValue, arrivalLocation.timezone) : date;
      const dateString = dateValue && zonedDateValue ? zonedDateValue.toString() : null;

      setValue('arrivalDateTimeValue', zonedDateValue);
      setValue('arrivalDateTime', dateString || '');

      if (formType === LegTypeEnum.ground && dateString) {
        const data = await calculateLegTimeDuration(legType, CalculateGroundTimeType.ArrivalTime, dateString);
        setLegDuration(formType, legType, data);
      }

      if (dateString && isDateTimeLinked && departureLocation.timezone) {
        const dateTimeValue = parseZonedDateTime(dateString)
          .add({ milliseconds: -timeDurationInMs })
          .toAbsoluteString();

        const dateTimeValueInLocationTz = parseAbsolute(dateTimeValue, departureLocation.timezone);

        setValue('departureDateTimeValue', dateTimeValueInLocationTz);
        setValue('departureDateTime', dateTimeValueInLocationTz.toString());
      }
    }
  };

  const isAviaForm = formType === LegTypeEnum.air;
  const isGroundForm = formType === LegTypeEnum.ground;
  const isCommercialAvia = legType === RouteLegTypeEnum.CommercialFlightAviaLeg;
  const isTmdxAviaLeg = legType === RouteLegTypeEnum.TmdxJetAviaLeg;
  const isAirport = arrivalLocation.locationType === LegLocationTypeEnum.airport;
  const isCustomWithCountriesSelect =
    arrivalLocation.locationType === LegLocationTypeEnum.custom && countriesList.length > 1;

  const gridTemplateColumns = isAviaForm
    ? `${isCommercialAvia ? '1fr 1fr' : '10rem minmax(0, 1fr) 13rem'}`
    : `10rem ${isAirport || isCustomWithCountriesSelect ? '7rem' : ''} minmax(0, 1fr) 13rem`;

  const isArrivalTimeDisabled = !isMainDataEdit || !arrivalLocation.id;

  return (
    <AirportLocationProvider locationType="arrivalLocation" handleDateTime={handleArrivalLocationDateTime}>
      <div
        className="grid gap-4 w-full relative z-19"
        style={{
          gridTemplateColumns,
          gridTemplateRows: '1fr',
        }}
      >
        {isGroundForm && (
          <SegmentLocation
            locationType="arrivalLocation"
            predefinedLocations={predefinedLocations}
            countriesList={countriesList}
            handleDateTime={handleArrivalLocationDateTime}
          />
        )}
        {isAirport && <AirportLocation />}
        <Controller
          name="arrivalDateTimeValue"
          control={control}
          render={({ field: { value, onChange, ...field }, fieldState: { invalid } }) => (
            <TmdxDatepicker
              size="md"
              aria-label="requiredArrivalTime"
              label="Arrival Time *"
              hideTimeZone
              hourCycle={24}
              isDisabled={isArrivalTimeDisabled}
              showMonthAndYearPickers={false}
              granularity="minute"
              value={value}
              onChange={handleDateTimeChange}
              isInvalid={invalid}
              {...field}
              endContent={
                <div className="pointer-events-none flex items-center">
                  <span className="text-default-400 text-small">
                    <IoMdTime size={16} />
                  </span>
                </div>
              }
            />
          )}
        />
      </div>
      {isTmdxAviaLeg && <RefuelAirportsBanner />}
    </AirportLocationProvider>
  );
};
